/**
 * A small sky for the cockpit to reflect.
 *
 * ## Why this is the piece that makes the other two work
 *
 * The cabin arrived with procedural grain, then with photographed normal and roughness maps, and
 * **neither was visible.** That is not a plumbing failure — it was verified in a browser that all
 * seven maps fetch and bind. It is what those maps *do*: a roughness map modulates specular
 * response and a normal map perturbs it, and this scene has no environment map, so there is no
 * specular response to modulate. A hemisphere fill gives an almost perfectly smooth diffuse
 * shading that varies only with the surface normal, which a fine grain barely moves.
 *
 * Give the same materials something to reflect and all of it appears at once: the vinyl's pebble,
 * the leather's break, the scuffs along the window frame, and the difference between a rough
 * moulding and a painted rail. That is the whole of what "the surfaces look like plastic" was.
 *
 * ## Scoped to the cockpit, deliberately
 *
 * `Scene.environment` would have been one line, and it would have applied image-based lighting to
 * the terrain, the water and the aircraft as well — a global change to the look of the whole
 * simulator, made as a side effect of working on an instrument panel. This is assigned as
 * `material.envMap` on the cockpit's own materials instead, so the blast radius is the cabin.
 *
 * `MeshStandardNodeMaterial` supports it: it overrides `setupEnvironment` to wrap the map in an
 * `EnvironmentNode`, which accepts an **equirectangular** texture and PMREM-filters it internally,
 * cached per renderer. Checked in three's source before building anything on it, because the last
 * assumption made about materials here — that a `metalness` of 0.55 was low enough to survive
 * having nothing to reflect — was wrong and put black girders across the windscreen.
 *
 * ## What is in it, and what deliberately is not
 *
 * Sky above, ground below, the horizon between them, taken from the same radiances the scene's own
 * hemisphere fill is built from — so the cabin reflects the sky it is actually flying under, and
 * goes red at dusk with everything else.
 *
 * **No sun disc.** The sun is already a `DirectionalLight` in this scene and already puts a
 * specular highlight on the cockpit; adding it here as well would count it twice. What is missing
 * without this file is not the sun, it is the other 99 % of the sky.
 *
 * ## Cost
 *
 * 64×32 RGBA float — 32 KB, rebuilt only when the sky has actually moved. That last part matters:
 * three regenerates the PMREM chain when the source texture updates, so rewriting it every frame
 * would put a filtering pass in the frame loop to describe a sky that changes over minutes.
 *
 * `updateFixed` / `updateFrame`: neither, in effect. {@link CockpitEnvironmentMap.setSky} is called
 * from the scene's per-frame environment update and returns immediately unless the sky has moved.
 */

import {
  DataTexture,
  EquirectangularReflectionMapping,
  FloatType,
  LinearFilter,
  LinearSRGBColorSpace,
  RGBAFormat,
  type Vector3,
} from 'three';

/** Equirectangular, and small: PMREM will blur it far past this resolution anyway. */
const WIDTH = 64;
const HEIGHT = 32;

/**
 * How far the sky has to move before the map is rebuilt.
 *
 * A dot product, so 0.9995 is about 1.8° of sun travel — a couple of minutes of real time, or one
 * step of the time-lapse slider. Below that the reflection in a 512-texel-wide vinyl pebble is not
 * measurably different and the PMREM pass is not worth running.
 */
const DIRECTION_EPSILON = 0.9995;
/** …and how much the radiance has to change, relatively, for the same reason. */
const RADIANCE_EPSILON = 0.02;

/** The three radiances a sky is made of, as the environment already publishes them. */
export interface SkyRadiances {
  /** Broad ambient — stands in for the zenith. */
  ambient: { r: number; g: number; b: number };
  /** Radiance at the horizon, where the sky meets the ground. */
  horizon: { r: number; g: number; b: number };
  /** Where the sun is, in render space. Used for nothing but deciding when to rebuild. */
  sunDirection: Vector3;
}

/**
 * The earth's share of what a cockpit reflects.
 *
 * Below the horizon the aeroplane is looking at ground, and ground is not a dimmer sky — it is
 * warm, and it is most of what lights the underside of a glareshield on a clear day. Taken as a
 * tint on the ambient rather than a colour of its own so that it tracks dusk with everything else.
 */
const GROUND_TINT = { r: 0.55, g: 0.48, b: 0.38 };

export class CockpitEnvironmentMap {
  readonly texture: DataTexture;

  private readonly data = new Float32Array(WIDTH * HEIGHT * 4);
  private readonly lastSunDirection = { x: 0, y: 0, z: 0 };
  private lastAmbientLuminance = -1;
  private built = false;

  constructor() {
    this.texture = new DataTexture(this.data, WIDTH, HEIGHT, RGBAFormat, FloatType);
    this.texture.mapping = EquirectangularReflectionMapping;
    // Radiance, not colour: an sRGB transfer function applied to light values is a sky that is
    // the wrong brightness everywhere and wrong by a different amount at each end.
    this.texture.colorSpace = LinearSRGBColorSpace;
    this.texture.minFilter = LinearFilter;
    this.texture.magFilter = LinearFilter;
    this.texture.generateMipmaps = false;
    this.texture.name = 'cockpit-environment';
  }

  /**
   * Rebuild the sky, if it has moved.
   *
   * @returns true when the texture was actually rewritten — for tests, and for anyone measuring
   *   how often the PMREM pass runs.
   */
  setSky(sky: SkyRadiances): boolean {
    const luminance = 0.2126 * sky.ambient.r + 0.7152 * sky.ambient.g + 0.0722 * sky.ambient.b;
    const direction = sky.sunDirection;
    const moved =
      this.lastSunDirection.x * direction.x +
        this.lastSunDirection.y * direction.y +
        this.lastSunDirection.z * direction.z <
      DIRECTION_EPSILON;
    const dimmed =
      Math.abs(luminance - this.lastAmbientLuminance) >
      RADIANCE_EPSILON * Math.max(luminance, this.lastAmbientLuminance, 1e-4);

    if (this.built && !moved && !dimmed) return false;

    this.lastSunDirection.x = direction.x;
    this.lastSunDirection.y = direction.y;
    this.lastSunDirection.z = direction.z;
    this.lastAmbientLuminance = luminance;
    this.built = true;

    /*
     * The zenith is brighter than the ambient it is derived from.
     *
     * `ambient` is the sky integrated over the hemisphere, so using it directly as the zenith
     * would render a sky uniformly at its own average — flat, which is the thing this file exists
     * to stop. 1.6 puts the zenith above the mean and the horizon below it, which is the way round
     * a real sky is and is what gives a curved surface a gradient to run through.
     */
    const zenith = {
      r: sky.ambient.r * 1.6,
      g: sky.ambient.g * 1.6,
      b: sky.ambient.b * 1.6,
    };
    const ground = {
      r: luminance * GROUND_TINT.r,
      g: luminance * GROUND_TINT.g,
      b: luminance * GROUND_TINT.b,
    };

    for (let j = 0; j < HEIGHT; j++) {
      /*
       * Row 0 is `v = 0`, and three's equirectangular convention puts `v = asin(y)/π + 0.5` — so
       * the first row of the buffer is straight *down* and the last is straight up. Getting this
       * upside down produces a cockpit lit from below by a blue sky, which is subtle enough on a
       * matte interior to survive a screenshot.
       */
      const v = (j + 0.5) / HEIGHT;
      const y = Math.sin((v - 0.5) * Math.PI);
      const up = Math.max(0, y);
      const down = Math.max(0, -y);
      // Raised toward the horizon, because a real sky's gradient is compressed near it.
      const upBlend = Math.pow(up, 0.6);
      const downBlend = Math.pow(down, 0.6);

      const r = sky.horizon.r + (zenith.r - sky.horizon.r) * upBlend + (ground.r - sky.horizon.r) * downBlend;
      const g = sky.horizon.g + (zenith.g - sky.horizon.g) * upBlend + (ground.g - sky.horizon.g) * downBlend;
      const b = sky.horizon.b + (zenith.b - sky.horizon.b) * upBlend + (ground.b - sky.horizon.b) * downBlend;

      for (let i = 0; i < WIDTH; i++) {
        // Azimuthally uniform: the sun is not in here (see the header), and without it there is
        // nothing in this sky that varies with heading.
        const offset = (j * WIDTH + i) * 4;
        this.data[offset] = r;
        this.data[offset + 1] = g;
        this.data[offset + 2] = b;
        this.data[offset + 3] = 1;
      }
    }

    this.texture.needsUpdate = true;
    return true;
  }

  dispose(): void {
    this.texture.dispose();
  }
}
